/* ==========================================================================
   Metamorfosis Magazine — motion.js
   One motion system for the whole theme. Vanilla JS, no build step.
   GSAP + ScrollTrigger are optional: everything degrades to IntersectionObserver
   + CSS transitions if the CDN is blocked or the client removes it.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var MOBILE  = window.matchMedia('(max-width: 767px)').matches;
  var FINE    = window.matchMedia('(pointer: fine)').matches;
  var HAS_GSAP = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

  if (HAS_GSAP) { window.gsap.registerPlugin(window.ScrollTrigger); }

  var M = window.Meta = window.Meta || {};
  M.reduced = REDUCED;
  M.mobile  = MOBILE;

  /* ---------- tiny helpers ---------- */
  function $(s, c) { return (c || doc).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || doc).querySelectorAll(s)); }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* Frame-throttled writer so several features can share one rAF */
  var rafQueue = [], rafPending = false;
  function onFrame(fn) {
    rafQueue.push(fn);
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(function (t) {
        rafPending = false;
        var q = rafQueue; rafQueue = [];
        for (var i = 0; i < q.length; i++) q[i](t);
      });
    }
  }

  /* ==========================================================================
     1. Header — shrink + blurred backdrop past 80px, dark-section logo swap
     ========================================================================== */
  function initHeader() {
    var header = $('.site-header');
    if (!header) return;

    function measure() {
      root.style.setProperty('--header-h', header.offsetHeight + 'px');
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });

    var scrolled = false;
    function onScroll() {
      var s = window.scrollY > 80;
      if (s !== scrolled) { scrolled = s; header.classList.toggle('is-scrolled', s); }
    }
    onScroll();
    window.addEventListener('scroll', function () { onFrame(onScroll); }, { passive: true });

    /* Swap to the cream logo while a dark section sits under the header */
    var darks = $$('[data-header-dark]');
    if (darks.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) e.target.__over = true;
          else e.target.__over = false;
        });
        header.classList.toggle('is-over-dark', darks.some(function (d) { return d.__over; }));
      }, { rootMargin: '-1px 0px -100% 0px', threshold: 0 });
      darks.forEach(function (d) { io.observe(d); });
    }

    /* Mobile overlay menu */
    var toggle = $('.nav-toggle', header);
    var overlay = $('#site-menu');
    if (toggle && overlay) {
      var lastFocus = null;
      function setOpen(open) {
        toggle.setAttribute('aria-expanded', String(open));
        overlay.classList.toggle('is-open', open);
        doc.body.classList.toggle('menu-open', open);
        if (open) { lastFocus = doc.activeElement; (overlay.querySelector('a, button') || overlay).focus(); }
        else if (lastFocus) { lastFocus.focus(); }
      }
      toggle.addEventListener('click', function () {
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });
      overlay.addEventListener('click', function (e) {
        if (e.target.closest('a') || e.target.hasAttribute('data-close')) setOpen(false);
      });
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) setOpen(false);
      });
    }
  }

  /* ==========================================================================
     2. Scroll progress ribbon
     ========================================================================== */
  function initProgress() {
    var bar = $('.progress-ribbon');
    if (!bar) return;
    function update() {
      var h = doc.body.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (h > 0 ? clamp(window.scrollY / h, 0, 1) : 0) + ')';
    }
    update();
    window.addEventListener('scroll', function () { onFrame(update); }, { passive: true });
    window.addEventListener('resize', function () { onFrame(update); }, { passive: true });
  }

  /* ==========================================================================
     3. Text splitting — words and characters
     ========================================================================== */
  function splitWords(el) {
    if (el.__split) return;
    el.__split = true;
    var i = 0;
    var lines = el.innerHTML.split(/<br\s*\/?>/i);
    el.innerHTML = lines.map(function (line) {
      var words = line.trim().split(/\s+/).filter(Boolean).map(function (w) {
        return '<span class="split-word" style="--w-i:' + (i++) + '">' + w + '</span>';
      }).join(' ');
      return '<span class="split-line">' + words + '</span>';
    }).join('');
  }

  function splitChars(el) {
    if (el.__split) return;
    el.__split = true;
    var i = 0;
    el.innerHTML = el.textContent.split('').map(function (ch) {
      if (ch === ' ') return '<span class="split-char" style="--c-i:' + (i++) + '">&nbsp;</span>';
      return '<span class="split-char" style="--c-i:' + (i++) + '">' + ch + '</span>';
    }).join('');
  }

  M.splitWords = splitWords;
  M.splitChars = splitChars;

  /* ==========================================================================
     4. Reveal engine — IntersectionObserver drives .is-in on everything
     ========================================================================== */
  function initReveals() {
    /* Prepare split text before observing so layout settles once */
    $$('[data-split="words"]').forEach(splitWords);
    $$('[data-split="chars"]').forEach(splitChars);

    /* Stagger indices for children of [data-stagger] */
    $$('[data-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-stagger'), 10) || 90;
      Array.prototype.forEach.call(group.children, function (child, i) {
        child.style.setProperty('--s-i', i);
        child.style.setProperty('--reveal-delay', (i * step) + 'ms');
      });
    });

    var targets = $$('[data-reveal], [data-split], .hairline, .shimmer, .is-in-target');
    if (!targets.length) return;

    if (REDUCED) {
      targets.forEach(function (t) { t.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    targets.forEach(function (t) { io.observe(t); });

    /* Rect sweep — the reveal engine cannot rely on IntersectionObserver alone.
       [data-reveal="wipe"/"wipe-x"] hide themselves with clip-path, which zeroes
       the element's intersection rect, so the observer that is supposed to reveal
       them never fires and they stay invisible forever. Carousel cards parked
       off-screen horizontally have the same problem. getBoundingClientRect ignores
       clip-path and horizontal offset, so this sweep breaks both deadlocks. */
    var pending = targets.slice();
    function sweep() {
      var vh = window.innerHeight;
      pending = pending.filter(function (t) {
        /* No lower bound: an element sitting ABOVE the viewport has already been
           scrolled past and must simply be shown. Lazy images settling during load
           shift elements upward without firing scroll or resize, so anything that
           slips above the fold that way would otherwise stay hidden forever. */
        if (t.getBoundingClientRect().top < vh * 0.92) {
          t.classList.add('is-in');
          io.unobserve(t);
          return false;
        }
        return true;
      });
      if (!pending.length) {
        window.removeEventListener('scroll', onSweep);
        window.removeEventListener('resize', onSweep);
        window.removeEventListener('load', onSweep);
      }
    }
    function onSweep() { onFrame(sweep); }
    window.addEventListener('scroll', onSweep, { passive: true });
    window.addEventListener('resize', onSweep, { passive: true });
    /* Images finishing after DOMContentLoaded are the main source of the shift above */
    window.addEventListener('load', onSweep);
    requestAnimationFrame(sweep);
  }

  /* ==========================================================================
     5. Butterfly particle fields
     Rendered as CSS-animated absolutely-positioned SVGs (transform/opacity only).
     ========================================================================== */
  var BFLY_PATH = null;

  function butterflySVG() {
    if (!BFLY_PATH) {
      var src = $('[data-butterfly-path]');
      BFLY_PATH = src ? src.getAttribute('data-butterfly-path') : '';
    }
    return '<svg viewBox="55.4 -0.4 58.2 57.5" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
           '<path fill="currentColor" d="' + BFLY_PATH + '"/></svg>';
  }

  function initParticles() {
    if (REDUCED) return;
    $$('[data-particles]').forEach(function (host) {
      var want = parseInt(host.getAttribute('data-particles'), 10) || 16;
      var count = MOBILE ? Math.max(6, Math.round(want * 0.55)) : want;
      var color = host.getAttribute('data-particle-color') || '';
      var frag = doc.createDocumentFragment();
      var svg = butterflySVG();

      var PATHS = ['', ' p--b', ' p--c', ' p--d'];
      for (var i = 0; i < count; i++) {
        var depth = i % 4;                      /* 0 = far, 3 = near */
        var near = depth === 3;
        var p = doc.createElement('span');
        /* Random flight path so the field never drifts in lockstep */
        p.className = 'p' + PATHS[Math.floor(Math.random() * PATHS.length)] + (near ? ' p--near' : '');
        p.innerHTML = svg;
        p.style.left = rand(-4, 100) + '%';
        p.style.setProperty('--p-size', Math.round(rand(14, 24) + depth * 12) + 'px');
        /* Depth drives opacity: far ones stay atmospheric, near ones actually read */
        p.style.setProperty('--p-op', (0.16 + depth * 0.13).toFixed(3));
        p.style.setProperty('--p-dur', rand(16, 38).toFixed(1) + 's');
        p.style.setProperty('--p-delay', (-rand(0, 38)).toFixed(1) + 's');
        /* Near butterflies beat faster, distant ones lazier */
        p.style.setProperty('--p-flutter', rand(0.34, 0.62 + depth * 0.28).toFixed(2) + 's');
        if (color) p.style.setProperty('--p-color', color);
        frag.appendChild(p);
      }
      host.appendChild(frag);
    });
  }

  /* ==========================================================================
     6. Off-screen + hidden-tab pausing
     ========================================================================== */
  function initPausing() {
    doc.addEventListener('visibilitychange', function () {
      doc.body.classList.toggle('is-hidden', doc.hidden);
    });

    var ambientHosts = $$('.section, .site-hero, .site-footer, .page-hero');
    if (!ambientHosts.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        e.target.classList.toggle('is-paused', !e.isIntersecting);
      });
    }, { rootMargin: '15% 0px 15% 0px' });
    ambientHosts.forEach(function (h) { io.observe(h); });
  }

  /* ==========================================================================
     7. Parallax (desktop only)
     ========================================================================== */
  function initParallax() {
    if (REDUCED || MOBILE) return;
    var items = $$('.parallax');
    if (!items.length) return;

    function update() {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-speed')) || 0.15;
        var progress = (r.top + r.height / 2 - vh / 2) / vh;   /* -1 .. 1 */
        el.style.setProperty('--py', (-progress * speed * 160).toFixed(2) + 'px');
      });
    }
    update();
    window.addEventListener('scroll', function () { onFrame(update); }, { passive: true });
    window.addEventListener('resize', function () { onFrame(update); }, { passive: true });
  }

  /* ==========================================================================
     8. Page background colour transitions between light and dark sections
     ========================================================================== */
  function initBackdrop() {
    var zones = $$('[data-page-bg]');
    if (!zones.length) return;
    var base = getComputedStyle(root).getPropertyValue('--cream').trim();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) e.target.__on = true; else e.target.__on = false;
      });
      var active = zones.filter(function (z) { return z.__on; }).pop();
      root.style.setProperty('--page-bg', active ? active.getAttribute('data-page-bg') : base);
    }, { rootMargin: '-45% 0px -45% 0px' });
    zones.forEach(function (z) { io.observe(z); });
  }

  /* ==========================================================================
     9. Animated counters
     ========================================================================== */
  function initCounters() {
    var els = $$('[data-count]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        var el = e.target;
        var target = parseFloat(el.getAttribute('data-count')) || 0;
        var dur = parseInt(el.getAttribute('data-count-dur'), 10) || 1800;
        var suffix = el.getAttribute('data-count-suffix') || '';
        var prefix = el.getAttribute('data-count-prefix') || '';
        var decimals = (String(target).split('.')[1] || '').length;

        if (REDUCED) { el.textContent = prefix + target.toFixed(decimals) + suffix; return; }

        var start = null;
        (function step(t) {
          if (start === null) start = t;
          var p = clamp((t - start) / dur, 0, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
          if (p < 1) requestAnimationFrame(step);
        })(performance.now());
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ==========================================================================
     10. Magnetic buttons
     ========================================================================== */
  function initMagnetic() {
    if (REDUCED || !FINE) return;
    $$('.magnetic').forEach(function (el) {
      var radius = parseInt(el.getAttribute('data-magnet'), 10) || 40;
      function move(e) {
        var r = el.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        var dist = Math.hypot(dx, dy);
        var max = Math.max(r.width, r.height) / 2 + radius;
        if (dist > max) { el.style.transform = ''; return; }
        var pull = (1 - dist / max) * 0.34;
        el.style.transform = 'translate3d(' + (dx * pull).toFixed(2) + 'px,' + (dy * pull).toFixed(2) + 'px,0)';
      }
      function reset() { el.style.transform = ''; }
      window.addEventListener('mousemove', function (e) { onFrame(function () { move(e); }); }, { passive: true });
      el.addEventListener('mouseleave', reset);
    });
  }

  /* ==========================================================================
     11. Card tilt
     ========================================================================== */
  function initTilt() {
    if (REDUCED || MOBILE || !FINE) return;
    $$('.tilt').forEach(function (el) {
      var max = parseFloat(el.getAttribute('data-tilt')) || 6;
      el.addEventListener('pointerenter', function () { el.classList.add('is-tilting'); });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        onFrame(function () {
          el.style.transform =
            'perspective(900px) rotateY(' + (px * max * 2).toFixed(2) + 'deg) rotateX(' +
            (-py * max * 2).toFixed(2) + 'deg) translate3d(0,-6px,0)';
        });
      });
      el.addEventListener('pointerleave', function () {
        el.classList.remove('is-tilting');
        el.style.transform = '';
      });
    });
  }

  /* ==========================================================================
     12. Custom butterfly cursor (fine pointers only)
     ========================================================================== */
  function initCursor() {
    if (REDUCED || !FINE || MOBILE) return;
    var el = doc.createElement('div');
    el.className = 'cursor-bfly';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = butterflySVG();
    doc.body.appendChild(el);

    var tx = window.innerWidth / 2, ty = window.innerHeight / 2, cx = tx, cy = ty, on = false;

    window.addEventListener('mousemove', function (e) {
      tx = e.clientX; ty = e.clientY;
      if (!on) { on = true; el.classList.add('is-on'); }
      var hot = e.target.closest && e.target.closest('a, button, .tilt, [role="button"]');
      el.classList.toggle('is-hot', !!hot);
    }, { passive: true });

    document.addEventListener('mouseleave', function () { on = false; el.classList.remove('is-on'); });

    (function loop() {
      cx += (tx - cx) * 0.16;
      cy += (ty - cy) * 0.16;
      var lean = clamp((tx - cx) * 1.5, -22, 22);
      el.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0) rotate(' + lean.toFixed(1) + 'deg)';
      requestAnimationFrame(loop);
    })();
  }

  /* ==========================================================================
     13. Pointer position broadcast (mouse-parallax layers use --mx / --my)
     ========================================================================== */
  function initPointerVars() {
    if (REDUCED || !FINE) return;
    $$('[data-pointer-scope]').forEach(function (scope) {
      scope.addEventListener('pointermove', function (e) {
        var r = scope.getBoundingClientRect();
        onFrame(function () {
          scope.style.setProperty('--mx', ((e.clientX - r.left) / r.width).toFixed(3));
          scope.style.setProperty('--my', ((e.clientY - r.top) / r.height).toFixed(3));
        });
      }, { passive: true });
      scope.addEventListener('pointerleave', function () {
        scope.style.setProperty('--mx', 0.5);
        scope.style.setProperty('--my', 0.5);
      });
    });
  }

  /* ==========================================================================
     14. Hero butterfly — stroke draw, then fill, then continuous drift
     ========================================================================== */
  function initHeroButterfly() {
    var mark = $('[data-bfly-draw]');
    if (!mark) return;
    var path = mark.querySelector('.bfly__p');
    if (!path) return;

    if (REDUCED) { mark.classList.add('is-filled'); return; }

    var len = 1400;
    try { len = Math.ceil(path.getTotalLength()); } catch (e) {}
    mark.style.setProperty('--dash', len);

    requestAnimationFrame(function () {
      mark.classList.add('is-drawn');
      setTimeout(function () { mark.classList.add('is-filled'); }, 2400);
    });
  }

  /* ==========================================================================
     15. Rotating tagline (vertical roll)
     ========================================================================== */
  function initRotator() {
    $$('[data-rotate]').forEach(function (host) {
      var items = $$('.rotate__item', host);
      if (items.length < 2) { if (items[0]) items[0].classList.add('is-current'); return; }
      var i = 0;
      items[0].classList.add('is-current');
      if (REDUCED) return;
      var every = parseInt(host.getAttribute('data-rotate'), 10) || 3600;
      setInterval(function () {
        if (doc.hidden) return;
        items[i].classList.remove('is-current');
        items[i].classList.add('is-out');
        var prev = items[i];
        setTimeout(function () { prev.classList.remove('is-out'); }, 900);
        i = (i + 1) % items.length;
        items[i].classList.add('is-current');
      }, every);
    });
  }

  /* ==========================================================================
     16. Draggable / horizontal carousels (featured stories)
     ========================================================================== */
  function initCarousels() {
    $$('[data-carousel]').forEach(function (root_) {
      var track = $('.carousel__track', root_);
      if (!track) return;
      var prev = $('[data-carousel-prev]', root_);
      var next = $('[data-carousel-next]', root_);

      function step() {
        var card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + 24 : 340;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });

      function syncButtons() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      track.addEventListener('scroll', function () { onFrame(syncButtons); }, { passive: true });
      window.addEventListener('resize', function () { onFrame(syncButtons); }, { passive: true });
      syncButtons();

      /* Drag to scroll — pointer events, desktop and touch alike */
      var down = false, startX = 0, startLeft = 0, moved = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;   /* native touch scroll is better */
        down = true; moved = 0;
        startX = e.clientX; startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved = Math.abs(dx);
        track.scrollLeft = startLeft - dx;
      });
      function end() {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
      }
      track.addEventListener('pointerup', end);
      track.addEventListener('pointerleave', end);
      track.addEventListener('click', function (e) { if (moved > 8) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }

  /* ==========================================================================
     17. Filters (directory / stories) with animated transitions
     ========================================================================== */
  function initFilters() {
    $$('[data-filter-group]').forEach(function (group) {
      var buttons = $$('[data-filter]', group);
      var items = $$('[data-cat]', group);
      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var want = btn.getAttribute('data-filter');
          buttons.forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
            b.setAttribute('aria-pressed', String(b === btn));
          });
          items.forEach(function (item, i) {
            var cats = (item.getAttribute('data-cat') || '').split(/\s+/);
            var show = want === 'all' || cats.indexOf(want) !== -1;
            item.style.setProperty('--reveal-delay', (i * 45) + 'ms');
            item.classList.toggle('is-hidden', !show);
          });
        });
      });
    });
  }

  /* ==========================================================================
     18. Countdown timers
     ========================================================================== */
  function initCountdowns() {
    var els = $$('[data-countdown]');
    if (!els.length) return;
    function pad(n) { return n < 10 ? '0' + n : String(n); }
    function tick() {
      var now = Date.now();
      els.forEach(function (el) {
        var target = Date.parse(el.getAttribute('data-countdown'));
        if (isNaN(target)) { el.classList.add('is-empty'); return; }
        var d = Math.max(0, target - now);
        var days = Math.floor(d / 86400000);
        var hrs  = Math.floor(d / 3600000) % 24;
        var mins = Math.floor(d / 60000) % 60;
        var secs = Math.floor(d / 1000) % 60;
        var out = el.querySelectorAll('[data-unit]');
        [days, hrs, mins, secs].forEach(function (v, i) {
          if (out[i]) out[i].textContent = i === 0 ? String(days) : pad(v);
        });
      });
    }
    tick();
    setInterval(function () { if (!doc.hidden) tick(); }, 1000);
  }

  /* ==========================================================================
     19. Newsletter butterfly-release burst on success
     ========================================================================== */
  function releaseBurst(host) {
    if (REDUCED || !host) return;
    var svg = butterflySVG();
    for (var i = 0; i < 14; i++) {
      var b = doc.createElement('span');
      b.className = 'burst-bfly';
      b.innerHTML = svg;
      b.style.setProperty('--bx', rand(-160, 160).toFixed(0) + 'px');
      b.style.setProperty('--by', rand(-220, -90).toFixed(0) + 'px');
      b.style.setProperty('--br', rand(-60, 60).toFixed(0) + 'deg');
      b.style.animationDelay = (i * 45) + 'ms';
      host.appendChild(b);
      setTimeout(function (n) { return function () { n.remove(); }; }(b), 2200 + i * 45);
    }
  }
  M.releaseBurst = releaseBurst;

  function initNewsletter() {
    $$('[data-newsletter]').forEach(function (host) {
      if (host.querySelector('[data-success]')) releaseBurst(host);
      var form = host.querySelector('form');
      if (form) form.addEventListener('submit', function () { releaseBurst(host); });
    });
  }

  /* ==========================================================================
     20. GSAP layer — scrubbed extras when the CDN is available
     ========================================================================== */
  function initGsap() {
    if (!HAS_GSAP || REDUCED) return;
    var gsap = window.gsap;

    /* Scrubbed image scale on tall editorial media */
    $$('[data-gsap-scrub]').forEach(function (el) {
      gsap.fromTo(el,
        { scale: 1.12 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
        });
    });

    /* Horizontal drift for decorative bands */
    $$('[data-gsap-drift]').forEach(function (el) {
      var amt = parseFloat(el.getAttribute('data-gsap-drift')) || 120;
      gsap.fromTo(el,
        { xPercent: -4 },
        {
          x: amt, ease: 'none',
          scrollTrigger: { trigger: el.closest('.section') || el, start: 'top bottom', end: 'bottom top', scrub: 1 }
        });
    });
  }

  /* ==========================================================================
     21. Lenis smooth scroll (optional, desktop only)
     ========================================================================== */
  function initLenis() {
    if (REDUCED || MOBILE || typeof window.Lenis === 'undefined') return;
    var lenis = new window.Lenis({ duration: 1.05, smoothWheel: true, wheelMultiplier: 0.9 });
    M.lenis = lenis;
    function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    if (HAS_GSAP) {
      lenis.on('scroll', window.ScrollTrigger.update);
    }
  }

  /* ==========================================================================
     Boot
     ========================================================================== */
  function boot() {
    initHeader();
    initProgress();
    initReveals();
    initParticles();
    initPausing();
    initParallax();
    initBackdrop();
    initCounters();
    initMagnetic();
    initTilt();
    initCursor();
    initPointerVars();
    initHeroButterfly();
    initRotator();
    initCarousels();
    initFilters();
    initCountdowns();
    initNewsletter();
    initGsap();
    initLenis();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Shopify theme editor: re-run for newly injected sections */
  doc.addEventListener('shopify:section:load', function () {
    initReveals(); initParticles(); initCounters(); initMagnetic(); initTilt();
    initCarousels(); initFilters(); initCountdowns(); initRotator(); initHeroButterfly();
    initPointerVars(); initParallax(); initBackdrop();
  });
})();
